import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSale, salePrice, saleAppliesTo, fmtPrice } from '../utils/sale';
import { shippingEstimate } from '../utils/shipping';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, getCartCount } = useCart();
  const sale = useSale();
  const onSale = cartItems.some((item) => saleAppliesTo(sale, item));
  const lineTotal = (item) => salePrice(item.price, sale, item) * item.quantity;
  const cartTotal = cartItems.reduce((t, item) => t + lineTotal(item), 0);

  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'robots'; document.head.appendChild(meta); }
    meta.content = 'noindex, nofollow';
    document.title = 'Shopping Cart - Rezvani Furniture Studio';
    return () => { if (meta) meta.content = 'index, follow'; };
  }, []);

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <div className="w-32 h-32 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag size={64} className="text-gray-400" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Cart is Empty</h2>
            <p className="text-gray-600 mb-8">Start shopping to add items to your cart</p>
            <Link
              to="/products"
              className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all space-x-2"
            >
              <span>Browse Products</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16 sm:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 sm:mb-8">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div key={item.cartId} className="bg-white rounded-2xl p-4 sm:p-6 shadow-md">
                <div className="flex gap-3 sm:gap-6">
                  {/* Product Image */}
                  <Link to={`/products/${item.id}`} className="flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-20 sm:w-32 sm:h-32 object-cover rounded-xl hover:opacity-90 transition-opacity"
                    />
                  </Link>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/products/${item.id}`}>
                      <h3 className="text-base sm:text-xl font-bold text-gray-900 hover:text-cyan-600 transition-colors mb-1 sm:mb-2 break-words">
                        {item.name}
                      </h3>
                    </Link>
                    <p className="text-gray-600 text-xs sm:text-sm mb-1">Color: {item.selectedColor}</p>
                    {shippingEstimate(item) && (
                      <p className="text-cyan-700 text-xs sm:text-sm font-medium mb-1" data-testid={`cart-ship-note-${item.cartId}`}>
                        {shippingEstimate(item)}
                      </p>
                    )}
                    {item.customizations && Object.keys(item.customizations).length > 0 && (
                      <div className="text-gray-600 text-xs sm:text-sm mb-2 sm:mb-3">
                        {Object.entries(item.customizations).map(([key, value]) => (
                          <p key={key}>{key}: {value}</p>
                        ))}
                      </div>
                    )}
                    {!(item.customizations && Object.keys(item.customizations).length > 0) && <div className="mb-2 sm:mb-3" />}
                    {saleAppliesTo(sale, item) ? (
                      <div className="mb-3 sm:mb-4">
                        <p className="text-lg sm:text-2xl font-bold text-cyan-600">
                          {fmtPrice(salePrice(item.price, sale, item))}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-400 line-through">
                          ${item.price.toLocaleString()}
                        </p>
                      </div>
                    ) : (
                      <p className="text-lg sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                        ${item.price.toLocaleString()}
                      </p>
                    )}

                    <div className="flex items-center justify-between gap-2">
                      {/* Quantity Controls */}
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <button
                          onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                        >
                          <Minus size={16} className="sm:w-[18px] sm:h-[18px]" />
                        </button>
                        <span className="text-base sm:text-xl font-semibold w-8 sm:w-12 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                        >
                          <Plus size={16} className="sm:w-[18px] sm:h-[18px]" />
                        </button>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => removeFromCart(item.cartId)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 sm:p-2 rounded-lg transition-all"
                      >
                        <Trash2 size={18} className="sm:w-5 sm:h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-lg lg:sticky lg:top-28">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 sm:mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Items ({getCartCount()})</span>
                  <span>{fmtPrice(cartTotal)}</span>
                </div>
                {onSale && (
                  <div className="flex justify-between text-cyan-600 font-medium" data-testid="cart-sale-savings">
                    <span>Sale discount</span>
                    <span>Applied</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className="text-green-600 font-medium">FREE</span>
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between text-xl font-bold text-gray-900">
                    <span>Total</span>
                    <span data-testid="cart-total">{fmtPrice(cartTotal)}</span>
                  </div>
                </div>
              </div>

              <Link to="/checkout">
                <Button className="w-full h-14 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-lg font-semibold rounded-xl transition-all duration-300 hover:shadow-xl">
                  Proceed to Checkout
                </Button>
              </Link>

              <Link to="/products">
                <button className="w-full mt-4 py-3 text-cyan-600 hover:text-cyan-700 font-medium transition-colors">
                  Continue Shopping
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
